import { FC, useEffect, useState } from 'react';
import {
  Calendar,
  Clock,
  Home,
  VideoCall,
  MobileArrowBottom,
  MobileArrowTop,
  Profile,
  CreditCard,
} from '@agendapro/emerald-icons';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import { useTheme } from 'styled-components';
import { Paragraph } from '@/UI';
import { formatAMPMHour } from '@/utils/formatAMPMHour';
import { SummaryItem } from './SummaryItem/SummaryItem';

import * as St from './ServiceCollapse.styles';
import { useLocation } from '@/services/locations';
import { ServiceReservationProps } from '@/context/ReservationsContext/ReservationContext.types';

export const ServiceCollapse: FC<{ services: ServiceReservationProps[] }> = ({ services }) => {
  dayjs.extend(isBetween);
  const router = useRouter();
  const { palette, migrateColors } = useTheme();
  const { t } = useTranslation();
  const { locationId } = router.query as { locationId: string };
  const { data: currentLocation } = useLocation(locationId);
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    if (services?.length === 1) {
      setOpen(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <St.Wrapper>
      <St.TitleWrapper>
        <Paragraph size="subHeadline" color={migrateColors('text.subtle', 'gray')}>{`${t(
          'YOUR_PICKED_SERVICES',
        )}`}</Paragraph>
        <Paragraph className="flex items-center" size="xsmall" color={migrateColors('text.subtle', 'gray')}>{`${
          services.length
        } ${t('SERVICES')}`}</Paragraph>
      </St.TitleWrapper>
      {services.map((service, index) => (
        <St.CustomCollapse
          open={open === index}
          collapsedHeight={72}
          key={service.internalId}
          className="styledCollapse"
        >
          <St.CollapseButton type="button" onClick={() => setOpen(open === index ? null : index)}>
            <div>
              <Paragraph weight="light" color={migrateColors('text.subtle', 'gray')} className="serviceNumber">
                {service.hasSession ? `${t('SESSION')} ` : `${t('SERVICE')} `}
                {`${index + 1} ${t('OF')} ${services.length}`}
              </Paragraph>
              <Paragraph className="serviceName" data-testid={`service_name_collapse_title_${index}`}>
                {service.hasSession ? services[0].name : service.name}
              </Paragraph>
            </div>
            <div>{open === index ? <MobileArrowTop size={20} /> : <MobileArrowBottom size={20} />}</div>
          </St.CollapseButton>
          <St.CollapseContent>
            <SummaryItem iconComponent={<Calendar size={20} />} itemData={`${service.dateFormatted}`} />
            {service.showDuration ? (
              <SummaryItem
                iconComponent={<Clock size={20} />}
                itemData={`${formatAMPMHour(service.hourSelected)} - ${formatAMPMHour(service.hourEnd)}`}
              />
            ) : (
              <SummaryItem iconComponent={<Clock size={20} />} itemData={formatAMPMHour(service.hourSelected)} />
            )}
            <SummaryItem iconComponent={<Profile size={20} />} itemData={service.serviceProvider?.publicName} />
            <SummaryItem
              iconComponent={service.isVideo ? <VideoCall size={20} /> : <Home size={20} />}
              itemData={service.isVideo ? `${t('VIDEO_CONFERENCE')}` : currentLocation?.locationName || ''}
            />
            {!!service.price && !service.hasSession && (
              <SummaryItem
                iconComponent={<CreditCard size={20} />}
                prices={{
                  hasCharlyPromotion: !!service.charlyDiscount,
                  inPromotionRange: dayjs(service.daySelected).isBetween(
                    service.charlyPromotionStartDate,
                    service.charlyPromotionEndDate,
                    'day',
                    '[]',
                  ),
                  payingPrice: service.charlyDiscount ? service.payingPriceCharly : service.payingPrice,
                  price: service.price,
                  shouldPayOnline: service.mustBePaidOnline,
                  onlineDownPaymentAmount: service.onlineDownPaymentAmount,
                }}
              />
            )}
          </St.CollapseContent>
        </St.CustomCollapse>
      ))}
    </St.Wrapper>
  );
};
